// frontend/src/components/Automations/scheduledRows.js
// Pure display-mapping + grouping for the unified Scheduled view (Group 6 PR C, D9).
// No React import — consumed by ScheduledView.jsx and covered directly by
// scheduledRows.test.js. Input rows are the normalized shape produced by
// GET /api/scheduled (D3): { id, source, typeLabel, name, cadence, nextRunAt,
// nextRunBasis, lastRunAt, status, rawStatus, owner, health, controls }.

const ICON_BY_SOURCE = {
  automation: 'bot',
  research: 'search',
  system: 'server',
};

// badgeVariant values are the exact .sol-badge--<variant> modifier suffixes
// defined in frontend/src/styles/primitives.css (success/warning/muted/error).
const BADGE_BY_STATUS = {
  active: { variant: 'success', label: 'Active' },
  paused: { variant: 'warning', label: 'Paused' },
  done: { variant: 'muted', label: 'Done' },
  error: { variant: 'error', label: 'Needs attention' },
};

const OP_META = {
  pause: { label: 'Pause', pendingLabel: 'Pausing…' },
  resume: { label: 'Resume', pendingLabel: 'Resuming…' },
  cancel: { label: 'Cancel', pendingLabel: 'Cancelling…' },
};

/**
 * Maps one normalized scheduled row (D3 shape) to its display-only fields.
 * Never mutates the input row. A row with no controls (system rows, done
 * rows) round-trips to an empty controls array — this function does not
 * invent controls the backend did not send.
 */
export function rowToDisplay(row) {
  const iconKey = ICON_BY_SOURCE[row?.source] || 'server';
  const badge = BADGE_BY_STATUS[row?.status] || BADGE_BY_STATUS.done;
  const sourceControls = Array.isArray(row?.controls) ? row.controls : [];
  const controls = sourceControls.map((c) => {
    const meta = OP_META[c.op] || { label: c.op, pendingLabel: `${c.op}…` };
    return {
      op: c.op,
      label: meta.label,
      pendingLabel: meta.pendingLabel,
      method: c.method,
      path: c.path,
    };
  });
  return {
    iconKey,
    badgeVariant: badge.variant,
    badgeLabel: badge.label,
    controls,
  };
}

export const GROUP_ORDER = ['attention', 'active', 'paused', 'done', 'engine'];

export const GROUP_LABELS = {
  attention: 'Needs attention',
  active: 'Active',
  paused: 'Paused',
  done: 'Done',
  engine: 'Engine',
};

function sortByNextRunThenName(rows) {
  return [...rows].sort((a, b) => {
    const aTime = a.nextRunAt ? new Date(a.nextRunAt).getTime() : null;
    const bTime = b.nextRunAt ? new Date(b.nextRunAt).getTime() : null;
    const aValid = Number.isFinite(aTime);
    const bValid = Number.isFinite(bTime);
    if (!aValid && !bValid) return String(a.name || '').localeCompare(String(b.name || ''));
    if (!aValid) return 1;
    if (!bValid) return -1;
    if (aTime !== bTime) return aTime - bTime;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

/**
 * Groups normalized rows into the five Scheduled-view buckets.
 * - owner === 'Engine' always lands in `engine`, regardless of status.
 * - Otherwise: status 'error' -> attention, 'active' -> active,
 *   'paused' -> paused, 'done' -> done ONLY when showFinished (else dropped).
 * Each bucket is sorted by nextRunAt ascending, nulls last, then by name.
 */
export function groupRows(rows, { showFinished = false } = {}) {
  const buckets = { attention: [], active: [], paused: [], done: [], engine: [] };
  for (const row of Array.isArray(rows) ? rows : []) {
    if (row.owner === 'Engine') {
      buckets.engine.push(row);
      continue;
    }
    if (row.status === 'error') {
      buckets.attention.push(row);
      continue;
    }
    if (row.status === 'active') {
      buckets.active.push(row);
      continue;
    }
    if (row.status === 'paused') {
      buckets.paused.push(row);
      continue;
    }
    if (row.status === 'done') {
      if (showFinished) buckets.done.push(row);
      continue;
    }
  }
  return {
    attention: sortByNextRunThenName(buckets.attention),
    active: sortByNextRunThenName(buckets.active),
    paused: sortByNextRunThenName(buckets.paused),
    done: sortByNextRunThenName(buckets.done),
    engine: sortByNextRunThenName(buckets.engine),
  };
}
